import { useApolloClient } from '@apollo/client';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonSpinner,
  IonTextarea,
  IonTitle,
  IonToolbar,
  isPlatform,
} from '@ionic/react';
import { close } from 'ionicons/icons';
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPaperPlane } from 'react-icons/fa';
import {
  ChatMessage,
  ListChatMessagesDocument,
  ListChatMessagesQuery,
  useGetChatMessageQuery,
} from '../../api/graphql/generated';
import { useHasFeature } from '../../api/hooks/feature.hooks';
import { useGetTenantPreference } from '../../api/hooks/preference.hooks';
import { trackCreateChatMessage } from '../../config/analytics';
import { RightAlignedButtons } from '../button/aligned-buttons';
import '../conversation/chat.css';
import {
  SupportOpenSource,
  useSupportContext,
} from '../support/support.context';
import { useSaveStatusToast } from '../toast/toast.gql';
import { useChatMessageHelper } from './chat-message.hooks';
import { useUserChatNotificationToastLazy } from './chat.hooks';

interface ConversationModalProps {
  chatId: string;
  messageId?: string;
  onClose: () => void;
  isPresented: boolean;
  onSend?: () => void;
}

const ConversationModal = (props: ConversationModalProps): ReactElement => {
  const { chatId, onClose, isPresented, messageId, onSend } = props;
  const { i18n } = useTranslation();

  const textarea = useRef<HTMLIonTextareaElement>(null);
  const [messageText, setMessageText] = useState<string | null | undefined>();
  const apolloClient = useApolloClient();

  const { open: openSupport } = useSupportContext();

  const { data: existingMessageData } = useGetChatMessageQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      input: {
        id: String(messageId),
      },
    },
    skip: messageId === undefined || messageId === null,
  });

  const existingMessage = existingMessageData?.getChatMessage;

  useEffect(() => {
    setMessageText(existingMessage?.message);
  }, [existingMessage?.message]);

  const {
    uploader,
    attachmentList,
    validateInput,
    sendMessage,
    isSendMessageLoading,
  } = useChatMessageHelper({
    allowRemove: true,
    chatId,
    threadId: existingMessage ? Number(existingMessage.threadId) : undefined,
    conversationId: messageId,
  });

  const { present: presentUserChatNotificationToast } =
    useUserChatNotificationToastLazy(chatId);

  const focus = useCallback(() => {
    if (isPresented && textarea.current) {
      textarea.current.setFocus();
      // this bit below puts the cursor at the end of the text area on browsers that support it
      const currentValue = textarea.current.value;
      textarea.current.value = '';
      textarea.current.value = currentValue;
    }
  }, [isPresented]);

  useEffect(() => {
    // this is a bit hacky
    const delay = isPlatform('capacitor') ? 650 : 200;
    setTimeout(() => {
      focus();
    }, delay);
  }, [focus]);

  const [sendError, setSendError] = useState(false);

  const onIconClick = useCallback(async () => {
    await sendMessage(messageText ?? '', {
      beforeSend: async () => {
        setSendError(false);
        presentUserChatNotificationToast();
      },
      afterSend: async (message) => {
        setMessageText(undefined);
        if (onSend) {
          onSend();
        }

        if (!messageId && message) {
          trackCreateChatMessage();

          // we're updating the list query to include the newly created message
          const cacheData = apolloClient.readQuery<ListChatMessagesQuery>({
            query: ListChatMessagesDocument,
            variables: {
              input: {
                chatId: chatId,
              },
            },
          });
          const messages: ChatMessage[] = [
            ...(cacheData?.listChatMessages.items ?? []),
          ];
          messages.unshift(message);
          apolloClient.writeQuery({
            query: ListChatMessagesDocument,
            variables: {
              input: {
                chatId: chatId,
              },
            },
            data: {
              listChatMessages: {
                ...cacheData?.listChatMessages,
                messages,
              },
            },
          });
        }
      },
      onError: () => {
        setSendError(true);
      },
    });
    onClose();
  }, [
    sendMessage,
    messageText,
    onClose,
    presentUserChatNotificationToast,
    onSend,
    messageId,
    apolloClient,
    chatId,
  ]);

  const isEditingExisting = messageId ? true : false;
  const isValid = validateInput(messageText ?? '');

  useSaveStatusToast({
    defaultErrorMessageKey: messageId
      ? 'common:chat_create_error'
      : 'common:chat_edit_error',
    error: sendError,
  });

  const hideSupportFeature = useHasFeature<boolean>('beacon-hide-support');
  const hideSupportNudgeTenantPref = useGetTenantPreference<boolean>(
    'chat.hirebotics_support_nudge'
  )?.value;

  const disableHireboticsSupportNudge =
    hideSupportFeature === true || hideSupportNudgeTenantPref === true;

  let placeholderText: string;

  if (isEditingExisting) {
    placeholderText = i18n.t('common:chat_edit_message');
  } else {
    placeholderText = disableHireboticsSupportNudge
      ? i18n.t('common:chat_new_conversation')
      : i18n.t('common:chat_new_conversation_hirebotics_support');
  }

  const isSendDisabled = !isValid || isSendMessageLoading;

  return (
    <>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot={'start'}>
            <IonButton onClick={onClose}>
              <IonIcon icon={close} size="large" />
            </IonButton>
          </IonButtons>
          <IonButtons slot={'end'}>
            <IonButton onClick={onIconClick} disabled={isSendDisabled}>
              {isSendMessageLoading ? (
                <IonSpinner name="dots" />
              ) : (
                <FaPaperPlane
                  className={isValid ? 'send-icon-valid' : 'send-icon-invalid'}
                  size={18}
                />
              )}
            </IonButton>
          </IonButtons>
          <IonTitle>
            {isEditingExisting
              ? i18n.t('common:chat_edit_message')
              : i18n.t('common:chat_new_conversation')}
          </IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="modal-content">
          <div onClick={focus} className="modal-text-area-wrapper">
            <IonTextarea
              placeholder={placeholderText}
              value={messageText}
              className="modal-text-area"
              autocapitalize="on"
              autoGrow={true}
              rows={1}
              ref={textarea}
              onIonInput={(e) => {
                setMessageText(e.detail.value);
              }}
            />
          </div>
          {attachmentList}
        </div>
      </IonContent>
      <IonFooter>
        <IonToolbar>
          {!disableHireboticsSupportNudge && (
            <IonButtons slot="start" style={{ paddingLeft: '10px' }}>
              <IonButton
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  openSupport({ source: SupportOpenSource.CHAT });
                }}
              >
                {i18n.t('common:hirebotics_support')}
              </IonButton>
            </IonButtons>
          )}
          <RightAlignedButtons>{uploader}</RightAlignedButtons>
        </IonToolbar>
      </IonFooter>
    </>
  );
};

export { ConversationModal };
